import { ReactNode } from 'react';

import { GppMaybeOutlined, ReportProblemOutlined } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';

import { useAuthContext } from 'context/AuthContext';

type ApplicationErrorModal = {
    type: 'permissions' | 'client' | 'user' | 'session' | 'authentication';
};

const MESSAGES: Record<ApplicationErrorModal['type'], ReactNode> = {
    permissions: 'Ocorreu um erro durante o carregamento das permissões. Faça login novamente.',
    client: 'Ocorreu um erro durante o carregamento dos dados do cliente. Faça login novamente.',
    user: 'Ocorreu um erro durante o carregamento dos dados do usuário. Faça login novamente.',
    session: 'Sua conexão expirou por inatividade ou tempo limite de segurança. Você precisará fazer seu login novamente.',
    authentication: (
        <>
            Ocorreu uma <b>falha na autenticação</b> da sua conta. Isso pode ser causado por um conflito de acesso com outros sistemas ou
            instabilidades na sessão.
            <br />
            <br />
            Para sua segurança, é necessário <b>fazer o login novamente</b>.
        </>
    ),
};

const TITLES = {
    permissions: 'Erro ao recuperar permissões',
    client: 'Erro ao recuperar dados do cliente',
    user: 'Erro ao recuperar dados do usuário',
    session: 'Sessão expirada',
    authentication: 'Falha na autenticação',
} as const;

const ICONS = {
    permissions: <ReportProblemOutlined color="error" sx={{ fontSize: 32 }} />,
    client: <ReportProblemOutlined color="error" sx={{ fontSize: 32 }} />,
    user: <ReportProblemOutlined color="error" sx={{ fontSize: 32 }} />,
    session: <ReportProblemOutlined color="error" sx={{ fontSize: 32 }} />,
    authentication: <GppMaybeOutlined color="warning" sx={{ fontSize: 32 }} />,
} as const;

export function ApplicationErrorModal({ type }: ApplicationErrorModal) {
    const { login } = useAuthContext();
    const isClientError = type === 'client';
    const isAuthenticationError = type === 'authentication';

    /**
     * Se for erro de cliente, recarrega a aplicação.
     * Caso contrário, faz login novamente.
     */
    const handleAction = () => {
        if (isClientError) {
            window.location.reload();
        } else if (login) {
            login();
        }
    };

    return (
        <Dialog
            open
            maxWidth="xs"
            fullWidth
            transitionDuration={0}
            slotProps={{
                backdrop: {
                    ...(type !== 'session' && { sx: { backgroundColor: 'common.white' } }),
                },
                paper: {
                    sx: { borderRadius: 2, p: 3 },
                },
            }}
        >
            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: 2,
                    p: 0,
                    pt: 1,
                    pb: 2.75,
                }}
            >
                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: (theme) => theme.alpha(
                            isAuthenticationError ? theme.palette.warning.main : theme.palette.error.main,
                            0.1
                        ),
                    }}
                >
                    {ICONS[type]}
                </Box>

                <Typography variant="h5" fontWeight="bold" color="text.primary">
                    {TITLES[type]}
                </Typography>

                <Typography variant="body1" color="text.secondary">
                    {MESSAGES[type]}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 0, justifyContent: 'center' }}>
                <Button
                    // fullWidth
                    size="large"
                    onClick={handleAction}
                    variant="contained"
                    sx={{
                        'backgroundColor': '#3a2d7c',
                        'textTransform': 'none',
                        'fontWeight': 'bold',
                        'py': 1.2,
                        '&:hover': { backgroundColor: '#2e2366' },
                    }}
                >
                    {isClientError ? 'Recarregar aplicação' : 'Voltar para o login'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
